import React from "react";
import LegalPage from "./LegalPage";

const UPDATED_EN = "Last updated: August 26, 2026";
const UPDATED_HY = "Վերջին թարմացումը՝ 26 օգոստոսի, 2026թ.";
const UPDATED_FR = "Dernière mise à jour : 26 août 2026";

const sectionsEn = [
  {
    id: "acceptance",
    heading: "1. Acceptance of These Terms",
    body: (
      <>
        <p>
          These Terms of Service ("<strong>Terms</strong>") are a legal agreement between you and Greenwich
          ("<strong>Greenwich</strong>," "<strong>we</strong>," "<strong>us</strong>," or "<strong>our</strong>"), operating
          the website, mobile-friendly web app, and related services available at greenwich.am and its
          subdomains (collectively, the "<strong>Service</strong>"). By creating an account, browsing programs, or
          otherwise using the Service, you agree to be bound by these Terms and by our <a href="/privacy">Privacy Policy</a>,
          which is incorporated into these Terms by reference. If you do not agree, do not use the Service.
        </p>
        <p>
          If you are using the Service on behalf of an organization (for example, to submit or manage program
          listings), you represent that you have the authority to bind that organization to these Terms, and
          "you" refers to both you and that organization.
        </p>
      </>
    ),
  },
  {
    id: "eligibility",
    heading: "2. Eligibility and Age Requirements",
    body: (
      <>
        <p>
          Greenwich is built for young people, and many of the opportunities listed on the Service are aimed at
          users roughly between 14 and 35 years old. You must be at least <strong>13 years old</strong> to create an
          account. If you are between 13 and 18 years old (or the age of legal majority in your place of
          residence, if lower), you may only use the Service with the involvement, knowledge, and consent of a
          parent or legal guardian, who agrees to these Terms on your behalf.
        </p>
        <p>
          By using the Service, you represent that all information you provide about your age, identity, and
          eligibility is accurate. We may ask for confirmation of your age or guardian consent at any time and
          may suspend or terminate accounts we reasonably believe were created in violation of this section.
        </p>
      </>
    ),
  },
  {
    id: "description",
    heading: "3. Description of the Service",
    body: (
      <>
        <p>Greenwich helps users discover and apply to scholarships, grants, fellowships, exchange programs,
          internships, and similar opportunities, and offers community features on top of that, including:</p>
        <ul>
          <li><strong>Program listings</strong> submitted by organizations, added by our team, or gathered from public
            sources;</li>
          <li><strong>AI Matching</strong>, which uses information you provide (such as your interests, age, and
            education level) to suggest programs that may fit you: a recommendation tool, not a guarantee of
            eligibility or acceptance;</li>
          <li><strong>Greenwich Buddy</strong>, a peer mentorship feature that connects users as mentors and mentees;</li>
          <li>Profiles, a feed, groups/communities, direct and group messaging, notifications, and a levels/XP
            system that recognizes activity on the platform; and</li>
          <li>A donations page allowing voluntary financial support of Greenwich, processed by a third-party
            payment provider.</li>
        </ul>
        <p>We may add, change, limit, or remove features of the Service at any time, with or without notice.</p>
      </>
    ),
  },
  {
    id: "accounts",
    heading: "4. Your Account",
    body: (
      <>
        <p>To use most of the Service you need an account, created either with an email and password or through a
          third-party sign-in provider (such as Google or Facebook). You agree to:</p>
        <ul>
          <li>provide accurate, current information when registering and keep it up to date;</li>
          <li>keep your password confidential and not share your account with anyone else;</li>
          <li>use your own real identity, impersonation and fake or bulk-created accounts are not allowed; and</li>
          <li>notify us promptly at greenwicharmenia@gmail.com if you suspect unauthorized use of your account.</li>
        </ul>
        <p>You are responsible for all activity that happens under your account, whether or not you authorized it,
          except to the extent it results from our failure to secure the Service.</p>
      </>
    ),
  },
  {
    id: "conduct",
    heading: "5. Acceptable Use",
    body: (
      <>
        <p>You agree not to, and not to help anyone else:</p>
        <ul>
          <li>harass, bully, threaten, or discriminate against another user, including in Buddy conversations,
            messages, groups, or profile content;</li>
          <li>impersonate any person or organization, or misrepresent your affiliation with one;</li>
          <li>post content that is unlawful, fraudulent, hateful, sexually explicit involving a minor, or that
            infringes someone else's intellectual property or privacy;</li>
          <li>use the Service to solicit money, personal data, or off-platform contact from other users under
            false pretenses;</li>
          <li>scrape, crawl, or use automated means to collect data from the Service, or attempt to reverse
            engineer, decompile, or access non-public areas of the Service or its systems;</li>
          <li>upload viruses or malicious code, or attempt to interfere with, overload, or disrupt the Service;</li>
          <li>submit a program listing that is fake, misleading, or that charges applicants an undisclosed
            application fee; or</li>
          <li>violate any applicable law, including the laws of the Republic of Armenia.</li>
        </ul>
        <p>We may remove content, restrict features, or suspend or terminate accounts that violate this section, at
          our reasonable discretion and, where practical, after a warning.</p>
      </>
    ),
  },
  {
    id: "content",
    heading: "6. Your Content and Our License to It",
    body: (
      <>
        <p><strong>Ownership.</strong> You retain ownership of the content you post (profile information, messages,
          group posts, photos, and anything else you submit ("<strong>User Content</strong>"). We don't claim ownership
          of it.</p>
        <p><strong>License to us.</strong> By posting User Content on the Service, you grant Greenwich a worldwide,
          non-exclusive, royalty-free license to host, store, reproduce, display, and distribute that content
          solely to operate, maintain, and improve the Service (for example, showing your profile to other users
          or your posts in a community feed). This license ends when you delete the content or your account,
          except for copies kept in backups for a reasonable period or content that has already been shared with
          other users in ways we cannot retract (like a message already read by its recipient).</p>
        <p><strong>Your responsibility.</strong> You are solely responsible for your User Content and for having the
          rights necessary to post it. We are not obligated to monitor User Content but may review, remove, or
          restrict access to it if we believe it violates these Terms or the law.</p>
      </>
    ),
  },
  {
    id: "programs-disclaimer",
    heading: "7. Third-Party Programs and Organizations",
    body: (
      <>
        <p>
          Program listings on Greenwich are provided by third-party organizations, submitted by our team from
          public sources, or aggregated automatically. <strong>Greenwich is not the organizer of these programs</strong> unless
          explicitly stated otherwise, and does not guarantee their accuracy, availability, deadlines, eligibility
          criteria, or outcomes. Details can change without notice on the organizer's side. Always verify
          important details, deadlines especially, directly with the organizing institution before relying on
          them or applying.
        </p>
        <p>
          Any application, correspondence, or agreement you enter into with a program organizer is strictly
          between you and that organizer. Greenwich is not a party to it and is not responsible for the conduct of
          organizers, the quality of their programs, or any dispute, loss, or harm arising from your participation
          in a third-party program.
        </p>
        <p>
          <strong>AI Matching</strong> ranks programs based on the information you provide and publicly available
          program data. It is a convenience tool, not professional advice, and does not guarantee that you are
          eligible for, or will be accepted into, any program it suggests.
        </p>
      </>
    ),
  },
  {
    id: "buddy",
    heading: "8. Greenwich Buddy (Mentorship)",
    body: (
      <>
        <p>
          Greenwich Buddy connects users who volunteer as mentors with users seeking mentorship, based on shared
          interests and preferences you provide. Mentors are members of the community, not Greenwich employees or
          agents, and are not vetted through a background-check process. Greenwich does not supervise, and is not
          responsible for, the content or outcome of any mentorship relationship or conversation that takes place
          through, or as a result of, this feature.
        </p>
        <p>
          You are responsible for your own safety in any interaction with another user. Do not share sensitive
          personal information (home address, financial details, national ID numbers) with a mentor or mentee. If
          you are under 18, discuss participation in Buddy with a parent or guardian, and immediately tell a
          trusted adult and report to us at greenwicharmenia@gmail.com if a conversation makes you uncomfortable
          or if anyone asks you to move communication off-platform, requests money, or behaves inappropriately. We
          take these reports seriously and may permanently remove a user from the Buddy feature or the Service.
        </p>
      </>
    ),
  },
  {
    id: "donations",
    heading: "9. Donations and Pro Subscription",
    body: (
      <>
        <p>
          <strong>Donations</strong> made through the Service are voluntary contributions to support Greenwich's
          operations and are processed by a third-party card-payment provider (currently Ameriabank); we do not
          receive or store your full card details. Donations are non-refundable except where required by
          applicable law or at our discretion. Unless we state otherwise in writing, donations to Greenwich are
          not tax-deductible.
        </p>
        <p>
          <strong>Greenwich Pro</strong> is an optional paid subscription (1, 3, or 6 months, at the price shown at
          checkout) that unlocks additional features for its duration; it does not renew automatically, you choose
          whether to purchase another period when it ends. Payment is processed by the same third-party
          card-payment provider. Because Pro access is granted immediately on payment and is a digital feature
          rather than a physical good, purchases are non-refundable once the subscription period has started,
          except where required by applicable law.
        </p>
      </>
    ),
  },
  {
    id: "ip",
    heading: "10. Intellectual Property",
    body: (
      <>
        <p>
          The Service, including its design, text, graphics, logos, and underlying software (but excluding User
          Content and third-party program listings), is owned by Greenwich or its licensors and is protected by
          copyright, trademark, and other intellectual property laws. Except for the limited right to use the
          Service as intended, we grant you no rights to our trademarks or branding without our prior written
          consent.
        </p>
      </>
    ),
  },
  {
    id: "termination",
    heading: "11. Suspension and Termination",
    body: (
      <>
        <p>
          You may stop using the Service and delete your account at any time from your account settings, or by
          asking us to do so at greenwicharmenia@gmail.com. We may suspend or terminate your access to the
          Service, with or without notice, if we reasonably believe you have violated these Terms, created risk or
          legal exposure for us or another user, or if we discontinue the Service. Sections of these Terms that by
          their nature should survive termination (including Sections 6, 7, 8, 9, 10, 13, 14, and 15) will
          survive.
        </p>
      </>
    ),
  },
  {
    id: "disclaimers",
    heading: "12. Disclaimers",
    body: (
      <>
        <p>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR
          IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR
          THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, ERROR-FREE, OR THAT PROGRAM INFORMATION IS ACCURATE OR
          CURRENT, TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW.
        </p>
      </>
    ),
  },
  {
    id: "liability",
    heading: "13. Limitation of Liability",
    body: (
      <>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, GREENWICH AND ITS TEAM WILL NOT BE LIABLE FOR ANY INDIRECT,
          INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR OPPORTUNITY,
          ARISING FROM YOUR USE OF THE SERVICE, YOUR PARTICIPATION IN A THIRD-PARTY PROGRAM, OR YOUR INTERACTIONS
          WITH OTHER USERS (INCLUDING THROUGH GREENWICH BUDDY), EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF
          SUCH DAMAGES. OUR TOTAL LIABILITY FOR ANY CLAIM RELATING TO THE SERVICE WILL NOT EXCEED THE GREATER OF
          (A) THE AMOUNT YOU PAID US, IF ANY, IN THE 12 MONTHS BEFORE THE CLAIM, OR (B) 50,000 AMD. Nothing in
          these Terms limits liability that cannot be limited under applicable law.
        </p>
      </>
    ),
  },
  {
    id: "indemnification",
    heading: "14. Indemnification",
    body: (
      <>
        <p>
          You agree to defend, indemnify, and hold harmless Greenwich and its team from any claim, loss, or
          expense (including reasonable legal fees) arising from your violation of these Terms, your User
          Content, or your interaction with a program organizer or another user of the Service.
        </p>
      </>
    ),
  },
  {
    id: "law",
    heading: "15. Governing Law and Disputes",
    body: (
      <>
        <p>
          These Terms are governed by the laws of the Republic of Armenia, without regard to conflict-of-law
          principles. Any dispute arising from these Terms or the Service will be subject to the exclusive
          jurisdiction of the competent courts of the Republic of Armenia, unless mandatory consumer-protection
          law where you live gives you the right to bring a claim elsewhere.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    heading: "16. Changes to These Terms",
    body: (
      <>
        <p>
          We may update these Terms from time to time. If we make material changes, we will post the updated
          Terms with a new "Last updated" date and, where appropriate, notify you (for example, by email or an
          in-app notice). Continuing to use the Service after changes take effect means you accept the updated
          Terms.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    heading: "17. Contact Us",
    body: (
      <>
        <p>
          Questions about these Terms can be sent to <a href="mailto:greenwicharmenia@gmail.com">greenwicharmenia@gmail.com</a>{" "}
          or by phone at +374 94 577 585.
        </p>
      </>
    ),
  },
];

const sectionsHy = [
  {
    id: "acceptance",
    heading: "1. Պայմանների ընդունումը",
    body: (
      <>
        <p>
          Սույն Օգտագործման պայմանները (այսուհետ՝ «<strong>Պայմաններ</strong>») իրավական համաձայնագիր են ձեր և
          Greenwich-ի («<strong>Greenwich</strong>», «<strong>մենք</strong>») միջև, որը շահագործում է
          greenwich.am կայքը, բջջային հավելվածին համարժեք վեբ-հավելվածը և հարակից ծառայությունները
          (միասին՝ «<strong>Ծառայություն</strong>»)։ Հաշիվ ստեղծելով, ծրագրերը դիտելով կամ Ծառայությունն այլ կերպ
          օգտագործելով՝ դուք համաձայնում եք սույն Պայմաններին և մեր{" "}
          <a href="/privacy">Գաղտնիության քաղաքականությանը</a>, որը մաս է կազմում սույն Պայմանների։ Եթե համաձայն
          չեք, մի օգտագործեք Ծառայությունը։
        </p>
        <p>
          Եթե Ծառայությունն օգտագործում եք կազմակերպության անունից (օրինակ՝ ծրագրեր հրապարակելու համար), դուք
          հավաստիացնում եք, որ իրավասու եք տվյալ կազմակերպությանը կապել սույն Պայմաններով, և «դուք»-ը վերաբերում է
          ինչպես ձեզ, այնպես էլ այդ կազմակերպությանը։
        </p>
      </>
    ),
  },
  {
    id: "eligibility",
    heading: "2. Տարիքային պահանջներ",
    body: (
      <>
        <p>
          Greenwich-ը ստեղծված է երիտասարդների համար, և Ծառայության վրա տեղադրված հնարավորությունների մեծ մասը
          ուղղված է մոտավորապես 14-35 տարեկան օգտատերերին։ Հաշիվ ստեղծելու համար դուք պետք է լինեք առնվազն{" "}
          <strong>13 տարեկան</strong>։ Եթե 13-ից 18 տարեկան եք (կամ ձեր բնակության վայրում սահմանված չափահասության
          տարիքից փոքր, եթե այն ավելի ցածր է), Ծառայությունն օգտագործելու իրավունք ունեք միայն ծնողի կամ օրինական
          խնամակալի իմացությամբ, մասնակցությամբ և համաձայնությամբ, ով ձեր անունից ընդունում է սույն Պայմանները։
        </p>
        <p>
          Ծառայությունն օգտագործելով՝ դուք հավաստիացնում եք, որ ձեր տարիքի, ինքնության և իրավասության վերաբերյալ
          տրամադրած ողջ տեղեկատվությունը ճշգրիտ է։ Մենք կարող ենք ցանկացած պահի պահանջել տարիքի կամ խնամակալի
          համաձայնության հաստատում և կասեցնել կամ դադարեցնել հաշիվներ, որոնք հիմնավոր կասկածով ստեղծվել են սույն
          կետի խախտմամբ։
        </p>
      </>
    ),
  },
  {
    id: "description",
    heading: "3. Ծառայության նկարագրությունը",
    body: (
      <>
        <p>Greenwich-ն օգնում է օգտատերերին գտնել և դիմել կրթաթոշակների, դրամաշնորհների, պրակտիկաների, փոխանակման
          ծրագրերի և նմանատիպ հնարավորությունների, ինչպես նաև առաջարկում է հանրային գործառույթներ՝</p>
        <ul>
          <li><strong>Ծրագրերի ցանկեր</strong>՝ ներկայացված կազմակերպությունների կողմից, ավելացված մեր թիմի կողմից,
            կամ հավաքագրված հանրային աղբյուրներից.</li>
          <li><strong>AI Matching</strong>՝ որն օգտագործում է ձեր տրամադրած տեղեկությունները (հետաքրքրություններ,
            տարիք, կրթական մակարդակ)՝ ձեզ հարմար ծրագրեր առաջարկելու համար. սա հանձնարարական գործիք է, ոչ թե
            իրավասության կամ ընդունման երաշխիք.</li>
          <li><strong>Greenwich Buddy</strong>՝ հասակակիցների մենթորության գործառույթ, որը կապում է օգտատերերին որպես
            մենթոր և մենթի.</li>
          <li>Պրոֆիլներ, նորությունների հոսք, խմբեր/համայնքներ, անհատական և խմբային նամակագրություն,
            ծանուցումներ և մակարդակների/XP համակարգ, որը ճանաչում է հարթակի վրա ակտիվությունը. և</li>
          <li>Նվիրատվության էջ, որը թույլ է տալիս կամավոր ֆինանսապես աջակցել Greenwich-ին՝ երրորդ կողմի վճարային
            համակարգի միջոցով։</li>
        </ul>
        <p>Մենք կարող ենք ցանկացած պահի ավելացնել, փոփոխել, սահմանափակել կամ հեռացնել Ծառայության գործառույթներ՝
          ծանուցումով կամ առանց դրա։</p>
      </>
    ),
  },
  {
    id: "accounts",
    heading: "4. Ձեր հաշիվը",
    body: (
      <>
        <p>Ծառայության մեծ մասն օգտագործելու համար անհրաժեշտ է հաշիվ՝ ստեղծված էլ. փոստով և գաղտնաբառով կամ երրորդ
          կողմի մուտքի ծառայության միջոցով (օրինակ՝ Google կամ Facebook)։ Դուք համաձայնում եք՝</p>
        <ul>
          <li>գրանցվելիս տրամադրել ճշգրիտ, արդի տեղեկություններ և պահել դրանք թարմացված.</li>
          <li>ձեր գաղտնաբառը պահել գաղտնի և չկիսվել հաշվով ուրիշների հետ.</li>
          <li>օգտագործել ձեր իրական ինքնությունը. կեղծ ինքնանալը և կեղծ կամ մասսայաբար ստեղծված հաշիվներն
            արգելված են. և</li>
          <li>անհապաղ տեղեկացնել մեզ greenwicharmenia@gmail.com հասցեով, եթե կասկածում եք ձեր հաշվի
            չարտոնված օգտագործման մասին։</li>
        </ul>
        <p>Դուք պատասխանատու եք ձեր հաշվի ներքո տեղի ունեցող ողջ ակտիվության համար՝ անկախ նրանից՝ դուք եք
          թույլատրել այն, թե ոչ, բացառությամբ այն դեպքերի, երբ դա բխում է Ծառայության անվտանգությունն
          ապահովելու մեր ձախողումից։</p>
      </>
    ),
  },
  {
    id: "conduct",
    heading: "5. Օգտագործման կանոններ",
    body: (
      <>
        <p>Դուք համաձայնում եք չկատարել, և ոչ մեկին չօգնել՝</p>
        <ul>
          <li>ոտնձգել, ահաբեկել, սպառնալ կամ խտրականություն դրսևորել այլ օգտատիրոջ նկատմամբ, այդ թվում՝ Buddy
            զրույցներում, նամակագրության, խմբերում կամ պրոֆիլի բովանդակության մեջ.</li>
          <li>ձևանալ որևէ անձ կամ կազմակերպություն, կամ խեղաթյուրել ձեր կապը նրա հետ.</li>
          <li>հրապարակել ապօրինի, խաբեական, ատելության բովանդակություն, անչափահասի հետ կապված սեռական
            բնույթի նյութեր, կամ որևէ մեկի մտավոր սեփականությունը կամ գաղտնիությունը խախտող բովանդակություն.</li>
          <li>օգտագործել Ծառայությունը կեղծ պատրվակով այլ օգտատերերից գումար, անձնական տվյալներ կամ
            հարթակից դուրս կապ պահանջելու համար.</li>
          <li>քերել (scrape), սողալ (crawl) կամ ավտոմատացված միջոցներով տվյալներ հավաքագրել Ծառայությունից, կամ
            փորձել հակադարձ ինժեներիա անել, ապակոդավորել կամ մուտք գործել Ծառայության ոչ հանրային հատվածներ.</li>
          <li>վիրուսներ կամ վնասակար կոդ վերբեռնել, կամ փորձել խաթարել, ծանրաբեռնել կամ խափանել Ծառայությունը.</li>
          <li>ներկայացնել կեղծ, մոլորեցնող ծրագրի հայտարարություն, կամ մեկը, որը դիմորդներից գաղտնի
            դիմումի վճար է գանձում. կամ</li>
          <li>խախտել որևէ կիրառելի օրենք, այդ թվում՝ Հայաստանի Հանրապետության օրենքները։</li>
        </ul>
        <p>Մենք կարող ենք հեռացնել բովանդակություն, սահմանափակել գործառույթներ, կամ կասեցնել/դադարեցնել սույն
          կետը խախտող հաշիվներ՝ մեր հիմնավոր հայեցողությամբ և, հնարավորության դեպքում, նախազգուշացումից հետո։</p>
      </>
    ),
  },
  {
    id: "content",
    heading: "6. Ձեր բովանդակությունը և մեր լիցենզիան",
    body: (
      <>
        <p><strong>Սեփականություն։</strong> Դուք պահպանում եք ձեր հրապարակած բովանդակության (պրոֆիլի
          տեղեկություններ, նամակագրություն, խմբի գրառումներ, լուսանկարներ և այլ ներկայացված նյութեր՝
          «<strong>Օգտատիրոջ բովանդակություն</strong>») սեփականությունը։ Մենք դրա նկատմամբ սեփականություն չենք
          հավակնում։</p>
        <p><strong>Մեզ տրվող լիցենզիա։</strong> Ծառայության վրա Օգտատիրոջ բովանդակություն հրապարակելով՝ դուք
          Greenwich-ին տրամադրում եք համաշխարհային, ոչ բացառիկ, ազատ լիցենզիա՝ այդ բովանդակությունը հյուրընկալելու,
          պահելու, վերարտադրելու, ցուցադրելու և տարածելու համար՝ բացառապես Ծառայության շահագործման, պահպանման և
          բարելավման նպատակով (օրինակ՝ ձեր պրոֆիլը ցուցադրելով այլ օգտատերերին)։ Այս լիցենզիան դադարում է, երբ
          ջնջում եք բովանդակությունը կամ ձեր հաշիվը, բացառությամբ բավարար ժամկետով պահվող պահուստային պատճենների
          կամ արդեն այլ օգտատերերի հետ կիսված բովանդակության (օրինակ՝ արդեն կարդացված նամակ)։</p>
        <p><strong>Ձեր պատասխանատվությունը։</strong> Դուք միայնակ պատասխանատու եք ձեր Օգտատիրոջ բովանդակության և
          այն հրապարակելու համար անհրաժեշտ իրավունքներ ունենալու համար։ Մենք պարտավոր չենք վերահսկել Օգտատիրոջ
          բովանդակությունը, սակայն կարող ենք վերանայել, հեռացնել կամ սահմանափակել դրա հասանելիությունը, եթե
          հավատում ենք, որ այն խախտում է սույն Պայմանները կամ օրենքը։</p>
      </>
    ),
  },
  {
    id: "programs-disclaimer",
    heading: "7. Երրորդ կողմի ծրագրեր և կազմակերպություններ",
    body: (
      <>
        <p>
          Greenwich-ի ծրագրերի ցանկերը տրամադրվում են երրորդ կողմի կազմակերպությունների կողմից, ներկայացվում են
          մեր թիմի կողմից հանրային աղբյուրներից, կամ հավաքագրվում են ավտոմատ։ <strong>Greenwich-ը այս ծրագրերի
          կազմակերպիչը չէ</strong>, եթե հստակ այլ բան նշված չէ, և չի երաշխավորում դրանց ճշգրտությունը,
          հասանելիությունը, վերջնաժամկետները, իրավասության չափանիշները կամ արդյունքները։ Մանրամասները կարող են
          փոփոխվել առանց ծանուցման կազմակերպչի կողմից։ Կարևոր մանրամասները, հատկապես վերջնաժամկետները, միշտ
          ստուգեք անմիջապես կազմակերպիչ հաստատության հետ՝ նախքան դրանց հենվելը կամ դիմելը։
        </p>
        <p>
          Կազմակերպչի հետ ձեր կնքած ցանկացած դիմում, նամակագրություն կամ համաձայնագիր բացառապես ձեր և տվյալ
          կազմակերպչի միջև է։ Greenwich-ը դրա կողմ չէ և պատասխանատու չէ կազմակերպիչների վարքագծի, նրանց ծրագրերի
          որակի կամ երրորդ կողմի ծրագրին ձեր մասնակցությունից բխող որևէ վեճի, կորստի կամ վնասի համար։
        </p>
        <p>
          <strong>AI Matching</strong>-ը ծրագրերը դասակարգում է ձեր տրամադրած տեղեկությունների և հանրայնորեն
          հասանելի ծրագրային տվյալների հիման վրա։ Դա հարմարության գործիք է, ոչ մասնագիտական խորհրդատվություն, և
          չի երաշխավորում, որ դուք իրավասու եք առաջարկվող որևէ ծրագրի համար կամ կընդունվեք դրան։
        </p>
      </>
    ),
  },
  {
    id: "buddy",
    heading: "8. Greenwich Buddy (Մենթորություն)",
    body: (
      <>
        <p>
          Greenwich Buddy-ն կապում է մենթորի դերում կամավոր հանդես եկող օգտատերերին մենթորություն փնտրող
          օգտատերերի հետ՝ ձեր տրամադրած ընդհանուր հետաքրքրությունների և նախապատվությունների հիման վրա։ Մենթորները
          համայնքի անդամներ են, ոչ թե Greenwich-ի աշխատակիցներ կամ ներկայացուցիչներ, և չեն անցնում նախապատմության
          ստուգում։ Greenwich-ը չի վերահսկում և պատասխանատու չէ այս գործառույթի միջոցով կամ դրա արդյունքում տեղի
          ունեցող մենթորության հարաբերության կամ նամակագրության բովանդակության կամ արդյունքի համար։
        </p>
        <p>
          Այլ օգտատիրոջ հետ ցանկացած փոխգործակցության մեջ դուք պատասխանատու եք ձեր սեփական անվտանգության համար։
          Մի կիսվեք զգայուն անձնական տեղեկություններով (տան հասցե, ֆինանսական տվյալներ, նույնականացման
          համարներ) մենթորի կամ մենթիի հետ։ Եթե 18 տարեկանից փոքր եք, Buddy-ին մասնակցությունը քննարկեք ծնողի
          կամ խնամակալի հետ, և անմիջապես տեղեկացրեք վստահելի մեծահասակի ու հայտնեք մեզ
          greenwicharmenia@gmail.com հասցեով, եթե որևէ նամակագրություն ձեզ անհարմար է դարձնում, կամ եթե ինչ-որ
          մեկը փորձում է հաղորդակցությունը հարթակից դուրս տեղափոխել, գումար պահանջում կամ անպատշաճ
          պահվածք ունի։ Մենք լրջորեն ենք վերաբերվում նման հայտարարություններին և կարող ենք մշտապես հեռացնել
          օգտատիրոջը Buddy գործառույթից կամ Ծառայությունից։
        </p>
      </>
    ),
  },
  {
    id: "donations",
    heading: "9. Նվիրատվություններ և Pro բաժանորդագրություն",
    body: (
      <>
        <p>
          <strong>Նվիրատվությունները</strong>, որոնք կատարվում են Ծառայության միջոցով, Greenwich-ի գործունեությանն
          աջակցելու կամավոր ներդրումներ են և մշակվում են երրորդ կողմի վճարային համակարգի կողմից (ներկայումս՝
          Ameriabank). մենք չենք ստանում կամ պահում ձեր քարտի ամբողջական տվյալները։ Նվիրատվությունները չեն
          վերադարձվում, բացառությամբ կիրառելի օրենքով պահանջվող կամ մեր հայեցողությամբ դեպքերի։ Եթե գրավոր այլ բան
          նշված չէ, Greenwich-ին տրված նվիրատվությունները հարկից չեն նվազեցվում։
        </p>
        <p>
          <strong>Greenwich Pro</strong>-ն ընտրովի վճարովի բաժանորդագրություն է (1, 3 կամ 6 ամիս, վճարման պահին
          նշված գնով), որը բացում է լրացուցիչ գործառույթներ իր ժամկետի ընթացքում. այն ինքնաբերաբար չի
          երկարաձգվում. դուք ինքներդ եք որոշում՝ գնել հաջորդ ժամկետը, թե ոչ, երբ ընթացիկը ավարտվում է։ Վճարումը
          մշակվում է նույն երրորդ կողմի վճարային համակարգի կողմից։ Քանի որ Pro հասանելիությունը տրամադրվում է
          անմիջապես վճարումից հետո և թվային ապրանք է, ոչ ֆիզիկական, գնումները չեն վերադարձվում բաժանորդագրության
          ժամկետն սկսվելուց հետո, բացառությամբ կիրառելի օրենքով պահանջվող դեպքերի։
        </p>
      </>
    ),
  },
  {
    id: "ip",
    heading: "10. Մտավոր սեփականություն",
    body: (
      <>
        <p>
          Ծառայությունը, ներառյալ դրա դիզայնը, տեքստը, գրաֆիկան, լոգոները և հիմքում ընկած ծրագրակազմը (բացառությամբ
          Օգտատիրոջ բովանդակության և երրորդ կողմի ծրագրերի ցանկերի), Greenwich-ի կամ նրա լիցենզատուների
          սեփականությունն է և պաշտպանված է հեղինակային իրավունքով, ապրանքային նշանով և մտավոր սեփականության այլ
          օրենքներով։ Ծառայությունը նախատեսված նպատակով օգտագործելու սահմանափակ իրավունքից բացի, մենք ձեզ որևէ
          իրավունք չենք տալիս մեր ապրանքային նշանների կամ բրենդինգի նկատմամբ առանց մեր նախնական գրավոր
          համաձայնության։
        </p>
      </>
    ),
  },
  {
    id: "termination",
    heading: "11. Կասեցում և դադարեցում",
    body: (
      <>
        <p>
          Դուք կարող եք ցանկացած պահի դադարեցնել Ծառայության օգտագործումը և ջնջել ձեր հաշիվը՝ հաշվի կարգավորումներից
          կամ մեզ greenwicharmenia@gmail.com հասցեով դիմելով։ Մենք կարող ենք կասեցնել կամ դադարեցնել ձեր մուտքը
          Ծառայությանը՝ ծանուցումով կամ առանց դրա, եթե հիմնավոր հավատում ենք, որ դուք խախտել եք սույն Պայմանները,
          ռիսկ կամ իրավական խնդիր եք ստեղծել մեզ կամ այլ օգտատիրոջ համար, կամ եթե դադարեցնում ենք Ծառայությունը։
          Սույն Պայմանների այն կետերը, որոնք իրենց բնույթով պետք է շարունակեն գործել դադարեցումից հետո (այդ
          թվում՝ 6-րդ, 7-րդ, 8-րդ, 9-րդ, 10-րդ, 13-րդ, 14-րդ և 15-րդ կետերը), կշարունակեն ուժի մեջ մնալ։
        </p>
      </>
    ),
  },
  {
    id: "disclaimers",
    heading: "12. Երաշխիքների բացառում",
    body: (
      <>
        <p>
          ԾԱՌԱՅՈՒԹՅՈՒՆԸ ՏՐԱՄԱԴՐՎՈՒՄ Է «ԻՆՉՊԵՍ ԿԱ» ԵՎ «ԻՆՉՊԵՍ ՀԱՍԱՆԵԼԻ Է» ՀԻՄՈՒՆՔՈՎ, ԱՌԱՆՑ ՈՐԵՎԷ ՏԵՍԱԿԻ ԱՆՀԱՄ
          ԿԱՄ ԳՈՐԾՈՂ ԵՐԱՇԽԻՔՆԵՐԻ, ԱՅԴ ԹՎՈՒՄ՝ ԱՊՐԱՆՔԱՅՆՈՒԹՅԱՆ, ՈՐՈՇԱԿԻ ՆՊԱՏԱԿԻՆ ՀԱՄԱՊԱՏԱՍԽԱՆԵԼՈՒ ԿԱՄ ԻՐԱՎՈՒՆՔՆԵՐԸ
          ՉԽԱԽՏԵԼՈՒ ԵՐԱՇԽԻՔՆԵՐ, ԿԱՄ ԱՅՆ, ՈՐ ԾԱՌԱՅՈՒԹՅՈՒՆԸ ԱՆԸՆԴՀԱՏ, ԱՆՎՏԱՆԳ, ԱՆՍԽԱԼ ԿԼԻՆԻ, ԿԱՄ ՈՐ ԾՐԱԳՐԱՅԻՆ
          ՏԵՂԵԿՈՒԹՅՈՒՆԸ ՃՇԳՐԻՏ ԿԱՄ ԱՐԴԻ Է, ԿԻՐԱՌԵԼԻ ՕՐԵՆՔՈՎ ԹՈՒՅԼԱՏՐՎԱԾ ԱՌԱՎԵԼԱԳՈՒՅՆ ՉԱՓՈՎ։
        </p>
      </>
    ),
  },
  {
    id: "liability",
    heading: "13. Պատասխանատվության սահմանափակում",
    body: (
      <>
        <p>
          ՕՐԵՆՔՈՎ ԹՈՒՅԼԱՏՐՎԱԾ ԱՌԱՎԵԼԱԳՈՒՅՆ ՉԱՓՈՎ, GREENWICH-Ը ԵՎ ՆՐԱ ԹԻՄԸ ՊԱՏԱՍԽԱՆԱՏՈՒ ՉԵՆ ԼԻՆԵԼՈՒ ՈՐԵՎԷ ԱՆՈՒՂՂԱԿԻ,
          ԱՊԱՀԱ, ՀԱՏՈՒԿ, ՀԵՏԵՎԱՆՔԱՅԻՆ ԿԱՄ ՊԱՏԺԻՉ ՎՆԱՍՆԵՐԻ, ԿԱՄ ՇԱՀՈՒՅԹԻ, ՏՎՅԱԼՆԵՐԻ ԿԱՄ ՀՆԱՐԱՎՈՐՈՒԹՅԱՆ ԿՈՐՍՏԻ
          ՀԱՄԱՐ, ԱՌԱՋԱՑԱԾ ԾԱՌԱՅՈՒԹՅԱՆ ՁԵՐ ՕԳՏԱԳՈՐԾՈՒՄԻՑ, ԵՐՐՈՐԴ ԿՈՂՄԻ ԾՐԱԳՐԻՆ ՁԵՐ ՄԱՍՆԱԿՑՈՒԹՅՈՒՆԻՑ, ԿԱՄ ԱՅԼ
          ՕԳՏԱՏԵՐԵՐԻ ՀԵՏ ՁԵՐ ՓՈԽԳՈՐԾԱԿՑՈՒԹՅՈՒՆԻՑ (ԱՅԴ ԹՎՈՒՄ՝ GREENWICH BUDDY-Ի ՄԻՋՈՑՈՎ), ԱՆԳԱՄ ԵԹԵ ՄԵԶ
          ՏԵՂԵԿԱՑՎԱԾ Է ԵՂԵԼ ՆՄԱՆ ՎՆԱՍՆԵՐԻ ՀՆԱՐԱՎՈՐՈՒԹՅԱՆ ՄԱՍԻՆ։ Ծառայության հետ կապված ցանկացած պահանջի համար մեր
          ընդհանուր պատասխանատվությունը չի գերազանցի (Ա) վերջին 12 ամիսների ընթացքում մեզ վճարած գումարը, եթե
          այդպիսին կա, կամ (Բ) 50,000 ՀՀ դրամը՝ որն էլ ավելի մեծ է։ Սույն Պայմաններից ոչինչ չի սահմանափակում
          պատասխանատվություն, որը կիրառելի օրենքով չի կարող սահմանափակվել։
        </p>
      </>
    ),
  },
  {
    id: "indemnification",
    heading: "14. Փոխհատուցում",
    body: (
      <>
        <p>
          Դուք համաձայնում եք պաշտպանել, փոխհատուցել և զերծ պահել Greenwich-ին և նրա թիմին ցանկացած պահանջից,
          կորստից կամ ծախսից (ներառյալ ողջամիտ իրավաբանական ծախսերը), որոնք բխում են սույն Պայմանների ձեր
          խախտումից, ձեր Օգտատիրոջ բովանդակությունից կամ ծրագրի կազմակերպչի կամ Ծառայության այլ օգտատիրոջ հետ
          ձեր փոխգործակցությունից։
        </p>
      </>
    ),
  },
  {
    id: "law",
    heading: "15. Կիրառելի իրավունք և վեճեր",
    body: (
      <>
        <p>
          Սույն Պայմանները կարգավորվում են Հայաստանի Հանրապետության օրենքներով՝ առանց հաշվի առնելու
          իրավունքների կոլիզիայի սկզբունքները։ Սույն Պայմաններից կամ Ծառայությունից բխող ցանկացած վեճ ենթակա է
          Հայաստանի Հանրապետության իրավասու դատարանների բացառիկ իրավասությանը, եթե ձեր բնակության վայրի
          պարտադիր սպառողների պաշտպանության օրենքը ձեզ իրավունք չի տալիս պահանջը ներկայացնել այլուր։
        </p>
      </>
    ),
  },
  {
    id: "changes",
    heading: "16. Փոփոխություններ սույն Պայմաններում",
    body: (
      <>
        <p>
          Մենք ժամանակ առ ժամանակ կարող ենք թարմացնել սույն Պայմանները։ Էական փոփոխությունների դեպքում մենք
          կհրապարակենք թարմացված Պայմանները նոր «Վերջին թարմացումը» ամսաթվով և, անհրաժեշտության դեպքում, կտեղեկացնենք
          ձեզ (օրինակ՝ էլ. փոստով կամ հավելվածի ներսում ծանուցումով)։ Փոփոխություններն ուժի մեջ մտնելուց հետո
          Ծառայության շարունակական օգտագործումը նշանակում է, որ դուք ընդունում եք թարմացված Պայմանները։
        </p>
      </>
    ),
  },
  {
    id: "contact",
    heading: "17. Կապ մեզ հետ",
    body: (
      <>
        <p>
          Սույն Պայմանների վերաբերյալ հարցերն ուղարկեք{" "}
          <a href="mailto:greenwicharmenia@gmail.com">greenwicharmenia@gmail.com</a> հասցեով կամ զանգահարեք
          +374 94 577 585 հեռախոսահամարով։
        </p>
      </>
    ),
  },
];

// French version, for the France/EU market (fr.greenwich.am). Two points are
// deliberately NOT a plain translation of the EN/HY text: the minimum age in
// §2 is 15, not 13 — France set its GDPR Art. 8 digital-consent age at 15,
// higher than the 13 used elsewhere — and §15 adds a mandatory-consumer-
// protection savings clause, since an EU consumer's local mandatory
// protections can't be waived by a choice-of-law/forum clause the way the
// EN/HY version (written with an Armenian user in mind) assumes.
const sectionsFr = [
  {
    id: "acceptance",
    heading: "1. Acceptation des présentes Conditions",
    body: (
      <>
        <p>
          Les présentes Conditions d'utilisation (les « <strong>Conditions</strong> ») constituent un accord
          juridique entre vous et Greenwich (« <strong>Greenwich</strong> », « <strong>nous</strong> »), qui exploite
          le site web, l'application web adaptée aux mobiles et les services associés accessibles à
          fr.greenwich.am et ses sous-domaines (ensemble, le « <strong>Service</strong> »). En créant un compte, en
          consultant les programmes, ou en utilisant le Service de toute autre manière, vous acceptez d'être lié
          par les présentes Conditions et par notre <a href="/privacy">Politique de confidentialité</a>, qui y est
          incorporée par référence. Si vous n'acceptez pas, n'utilisez pas le Service.
        </p>
        <p>
          Si vous utilisez le Service au nom d'une organisation (par exemple pour publier ou gérer des annonces de
          programmes), vous garantissez avoir l'autorité nécessaire pour engager cette organisation par les
          présentes Conditions, et « vous » désigne alors à la fois vous-même et cette organisation.
        </p>
      </>
    ),
  },
  {
    id: "eligibility",
    heading: "2. Éligibilité et conditions d'âge",
    body: (
      <>
        <p>
          Greenwich est conçu pour les jeunes, et de nombreuses opportunités proposées sur le Service s'adressent
          à des utilisateurs âgés d'environ 14 à 35 ans. Vous devez avoir au moins <strong>15 ans</strong> pour créer
          un compte. Si vous avez entre 15 et 18 ans, vous ne pouvez utiliser le Service qu'avec l'implication, la
          connaissance et le consentement d'un parent ou tuteur légal, qui accepte les présentes Conditions en
          votre nom.
        </p>
        <p>
          En utilisant le Service, vous garantissez que toutes les informations fournies concernant votre âge,
          votre identité et votre éligibilité sont exactes. Nous pouvons demander à tout moment une confirmation
          de votre âge ou du consentement de votre représentant légal, et pouvons suspendre ou résilier tout
          compte créé, selon nous raisonnablement, en violation de la présente section.
        </p>
      </>
    ),
  },
  {
    id: "description",
    heading: "3. Description du Service",
    body: (
      <>
        <p>Greenwich aide les utilisateurs à découvrir et à postuler à des bourses, subventions, programmes
          d'échange, stages et opportunités similaires, et propose en complément des fonctionnalités
          communautaires, notamment :</p>
        <ul>
          <li><strong>Des annonces de programmes</strong> soumises par des organisations, ajoutées par notre équipe,
            ou collectées à partir de sources publiques ;</li>
          <li><strong>AI Matching</strong>, qui utilise les informations que vous fournissez (centres d'intérêt, âge,
            niveau d'études) pour vous suggérer des programmes pertinents : un outil de recommandation, non une
            garantie d'éligibilité ou d'admission ;</li>
          <li><strong>Greenwich Buddy</strong>, une fonctionnalité de mentorat entre pairs qui met en relation des
            utilisateurs en tant que mentors et mentorés ;</li>
          <li>Des profils, un fil d'actualité, des groupes/communautés, une messagerie privée et de groupe, des
            notifications, et un système de niveaux/XP reconnaissant l'activité sur la plateforme ; et</li>
          <li>Une page de dons permettant de soutenir financièrement Greenwich sur une base volontaire, traitée
            par un prestataire de paiement tiers.</li>
        </ul>
        <p>Nous pouvons ajouter, modifier, limiter ou supprimer des fonctionnalités du Service à tout moment, avec
          ou sans préavis.</p>
      </>
    ),
  },
  {
    id: "accounts",
    heading: "4. Votre compte",
    body: (
      <>
        <p>L'utilisation de la majeure partie du Service nécessite un compte, créé soit avec un e-mail et un mot
          de passe, soit via un fournisseur de connexion tiers (comme Google ou Facebook). Vous acceptez de :</p>
        <ul>
          <li>fournir des informations exactes et à jour lors de votre inscription, et de les maintenir à jour ;</li>
          <li>garder votre mot de passe confidentiel et ne pas partager votre compte avec autrui ;</li>
          <li>utiliser votre véritable identité, l'usurpation d'identité et les comptes falsifiés ou créés en
            masse sont interdits ; et</li>
          <li>nous informer rapidement à greenwicharmenia@gmail.com si vous soupçonnez une utilisation non
            autorisée de votre compte.</li>
        </ul>
        <p>Vous êtes responsable de toute activité se produisant sous votre compte, que vous l'ayez autorisée ou
          non, sauf dans la mesure où elle résulte de notre manquement à sécuriser le Service.</p>
      </>
    ),
  },
  {
    id: "conduct",
    heading: "5. Utilisation acceptable",
    body: (
      <>
        <p>Vous acceptez de ne pas, et de ne pas aider autrui à :</p>
        <ul>
          <li>harceler, intimider, menacer ou discriminer un autre utilisateur, y compris dans les conversations
            Buddy, les messages, les groupes ou le contenu de profil ;</li>
          <li>usurper l'identité d'une personne ou d'une organisation, ou présenter de manière trompeuse votre
            affiliation avec celle-ci ;</li>
          <li>publier un contenu illégal, frauduleux, haineux, à caractère sexuel impliquant un mineur, ou
            portant atteinte à la propriété intellectuelle ou à la vie privée d'autrui ;</li>
          <li>utiliser le Service pour solliciter de l'argent, des données personnelles, ou un contact hors
            plateforme auprès d'autres utilisateurs sous de faux prétextes ;</li>
          <li>extraire (scraping), explorer (crawling) ou collecter des données du Service par des moyens
            automatisés, ou tenter de procéder à une ingénierie inverse, de décompiler, ou d'accéder à des zones
            non publiques du Service ou de ses systèmes ;</li>
          <li>téléverser des virus ou code malveillant, ou tenter de perturber, surcharger ou interrompre le
            Service ;</li>
          <li>soumettre une annonce de programme fausse, trompeuse, ou facturant aux candidats des frais de
            candidature non divulgués ; ou</li>
          <li>enfreindre toute loi applicable.</li>
        </ul>
        <p>Nous pouvons retirer du contenu, restreindre des fonctionnalités, ou suspendre/résilier des comptes
          enfreignant la présente section, à notre discrétion raisonnable et, lorsque cela est possible, après
          avertissement.</p>
      </>
    ),
  },
  {
    id: "content",
    heading: "6. Votre contenu et notre licence sur celui-ci",
    body: (
      <>
        <p><strong>Propriété.</strong> Vous conservez la propriété du contenu que vous publiez (informations de
          profil, messages, publications de groupe, photos, et tout autre contenu soumis (« <strong>Contenu
          Utilisateur</strong> »). Nous ne revendiquons aucune propriété sur celui-ci.</p>
        <p><strong>Licence qui nous est accordée.</strong> En publiant du Contenu Utilisateur sur le Service, vous
          accordez à Greenwich une licence mondiale, non exclusive et gratuite pour héberger, stocker, reproduire,
          afficher et distribuer ce contenu, uniquement afin d'exploiter, maintenir et améliorer le Service (par
          exemple, afficher votre profil à d'autres utilisateurs ou vos publications dans un fil communautaire).
          Cette licence prend fin lorsque vous supprimez le contenu ou votre compte, sauf pour les copies
          conservées dans des sauvegardes pendant une durée raisonnable, ou le contenu déjà partagé avec d'autres
          utilisateurs d'une manière que nous ne pouvons pas annuler (comme un message déjà lu par son
          destinataire).</p>
        <p><strong>Votre responsabilité.</strong> Vous êtes seul responsable de votre Contenu Utilisateur et de la
          détention des droits nécessaires pour le publier. Nous ne sommes pas tenus de surveiller le Contenu
          Utilisateur, mais pouvons l'examiner, le retirer ou en restreindre l'accès si nous estimons qu'il enfreint
          les présentes Conditions ou la loi.</p>
      </>
    ),
  },
  {
    id: "programs-disclaimer",
    heading: "7. Programmes et organisations tiers",
    body: (
      <>
        <p>
          Les annonces de programmes sur Greenwich sont fournies par des organisations tierces, soumises par notre
          équipe à partir de sources publiques, ou agrégées automatiquement. <strong>Greenwich n'est pas
          l'organisateur de ces programmes</strong>, sauf mention explicite contraire, et ne garantit ni leur
          exactitude, ni leur disponibilité, ni leurs délais, ni leurs critères d'éligibilité, ni leurs résultats.
          Les détails peuvent changer sans préavis du côté de l'organisateur. Vérifiez toujours les informations
          importantes, en particulier les délais, directement auprès de l'institution organisatrice avant de
          vous y fier ou de postuler.
        </p>
        <p>
          Toute candidature, correspondance ou accord que vous concluez avec un organisateur de programme relève
          strictement de vous et de cet organisateur. Greenwich n'y est pas partie et n'est pas responsable de la
          conduite des organisateurs, de la qualité de leurs programmes, ni de tout litige, perte ou préjudice
          résultant de votre participation à un programme tiers.
        </p>
        <p>
          <strong>AI Matching</strong> classe les programmes en fonction des informations que vous fournissez et des
          données publiquement disponibles sur les programmes. Il s'agit d'un outil de confort, non d'un conseil
          professionnel, et il ne garantit pas que vous êtes éligible à un programme suggéré, ni que vous y serez
          admis.
        </p>
      </>
    ),
  },
  {
    id: "buddy",
    heading: "8. Greenwich Buddy (Mentorat)",
    body: (
      <>
        <p>
          Greenwich Buddy met en relation des utilisateurs se portant volontaires comme mentors avec des
          utilisateurs recherchant un mentorat, sur la base des centres d'intérêt et préférences communs que vous
          indiquez. Les mentors sont des membres de la communauté, non des employés ou agents de Greenwich, et ne
          font l'objet d'aucune vérification d'antécédents. Greenwich ne supervise pas, et n'est pas responsable
          du contenu ou du résultat d'une relation de mentorat ou d'une conversation ayant lieu via cette
          fonctionnalité ou en découlant.
        </p>
        <p>
          Vous êtes responsable de votre propre sécurité dans toute interaction avec un autre utilisateur. Ne
          partagez pas d'informations personnelles sensibles (adresse personnelle, données financières, numéros
          d'identité) avec un mentor ou un mentoré. Si vous avez moins de 18 ans, discutez de votre participation à
          Buddy avec un parent ou tuteur, et informez immédiatement un adulte de confiance ainsi que nous-mêmes à
          greenwicharmenia@gmail.com si une conversation vous met mal à l'aise, ou si quelqu'un tente de déplacer
          la communication hors plateforme, demande de l'argent, ou a un comportement inapproprié. Nous prenons ces
          signalements au sérieux et pouvons retirer définitivement un utilisateur de la fonctionnalité Buddy ou du
          Service.
        </p>
      </>
    ),
  },
  {
    id: "donations",
    heading: "9. Dons et abonnement Pro",
    body: (
      <>
        <p>
          Les <strong>dons</strong> effectués via le Service sont des contributions volontaires destinées à
          soutenir le fonctionnement de Greenwich et sont traités par un prestataire de paiement par carte tiers
          (actuellement Ameriabank) ; nous ne recevons ni ne conservons vos coordonnées bancaires complètes. Les
          dons ne sont pas remboursables, sauf si la loi applicable l'exige ou à notre discrétion. Sauf mention
          écrite contraire de notre part, les dons à Greenwich ne sont pas déductibles d'impôt.
        </p>
        <p>
          <strong>Greenwich Pro</strong> est un abonnement payant optionnel (1, 3 ou 6 mois, au prix indiqué lors du
          paiement) qui débloque des fonctionnalités supplémentaires pour sa durée ; il ne se renouvelle pas
          automatiquement, vous décidez d'acheter ou non une nouvelle période à son expiration. Le paiement est
          traité par le même prestataire de paiement par carte tiers. L'accès Pro étant accordé immédiatement après
          le paiement et constituant un contenu numérique plutôt qu'un bien physique, les achats ne sont pas
          remboursables une fois la période d'abonnement commencée, sauf si la loi applicable l'exige, y compris,
          le cas échéant, votre droit de rétractation en tant que consommateur de l'UE, dans les limites où la loi
          applicable permet de renoncer à ce droit pour un contenu numérique fourni immédiatement avec votre accord
          exprès.
        </p>
      </>
    ),
  },
  {
    id: "ip",
    heading: "10. Propriété intellectuelle",
    body: (
      <>
        <p>
          Le Service, y compris sa conception, ses textes, graphismes, logos et logiciels sous-jacents (à
          l'exclusion du Contenu Utilisateur et des annonces de programmes tiers), appartient à Greenwich ou à ses
          concédants de licence et est protégé par le droit d'auteur, le droit des marques et d'autres lois sur la
          propriété intellectuelle. Hormis le droit limité d'utiliser le Service comme prévu, nous ne vous
          accordons aucun droit sur nos marques ou notre identité visuelle sans notre consentement écrit préalable.
        </p>
      </>
    ),
  },
  {
    id: "termination",
    heading: "11. Suspension et résiliation",
    body: (
      <>
        <p>
          Vous pouvez cesser d'utiliser le Service et supprimer votre compte à tout moment depuis les paramètres de
          votre compte, ou en nous le demandant à greenwicharmenia@gmail.com. Nous pouvons suspendre ou résilier
          votre accès au Service, avec ou sans préavis, si nous estimons raisonnablement que vous avez enfreint les
          présentes Conditions, créé un risque ou une exposition juridique pour nous ou un autre utilisateur, ou si
          nous cessons le Service. Les sections des présentes Conditions qui, de par leur nature, doivent survivre
          à la résiliation (y compris les sections 6, 7, 8, 9, 10, 13, 14 et 15) y survivront.
        </p>
      </>
    ),
  },
  {
    id: "disclaimers",
    heading: "12. Avertissements",
    body: (
      <>
        <p>
          LE SERVICE EST FOURNI « EN L'ÉTAT » ET « SELON DISPONIBILITÉ », SANS GARANTIE D'AUCUNE SORTE, EXPRESSE
          OU IMPLICITE, Y COMPRIS LES GARANTIES DE QUALITÉ MARCHANDE, D'ADÉQUATION À UN USAGE PARTICULIER, DE
          NON-CONTREFAÇON, OU QUE LE SERVICE SERA ININTERROMPU, SÉCURISÉ, EXEMPT D'ERREURS, OU QUE LES INFORMATIONS
          SUR LES PROGRAMMES SONT EXACTES OU À JOUR, DANS LA MESURE MAXIMALE PERMISE PAR LA LOI APPLICABLE. LA
          PRÉSENTE SECTION N'EXCLUT PAS LES GARANTIES LÉGALES QUI NE PEUVENT ÊTRE EXCLUES EN VERTU DU DROIT DE LA
          CONSOMMATION APPLICABLE.
        </p>
      </>
    ),
  },
  {
    id: "liability",
    heading: "13. Limitation de responsabilité",
    body: (
      <>
        <p>
          DANS LA MESURE MAXIMALE PERMISE PAR LA LOI, GREENWICH ET SON ÉQUIPE NE POURRONT ÊTRE TENUS RESPONSABLES
          DE TOUT DOMMAGE INDIRECT, ACCESSOIRE, SPÉCIAL, CONSÉCUTIF OU PUNITIF, NI DE TOUTE PERTE DE PROFITS, DE
          DONNÉES OU D'OPPORTUNITÉ, RÉSULTANT DE VOTRE UTILISATION DU SERVICE, DE VOTRE PARTICIPATION À UN
          PROGRAMME TIERS, OU DE VOS INTERACTIONS AVEC D'AUTRES UTILISATEURS (Y COMPRIS VIA GREENWICH BUDDY), MÊME
          SI NOUS AVIONS ÉTÉ INFORMÉS DE LA POSSIBILITÉ DE TELS DOMMAGES. NOTRE RESPONSABILITÉ TOTALE POUR TOUTE
          RÉCLAMATION LIÉE AU SERVICE NE DÉPASSERA PAS LE MONTANT LE PLUS ÉLEVÉ ENTRE (A) LES SOMMES QUE VOUS NOUS
          AVEZ VERSÉES, LE CAS ÉCHÉANT, AU COURS DES 12 MOIS PRÉCÉDANT LA RÉCLAMATION, OU (B) L'ÉQUIVALENT EN AMD DE
          100 EUROS. Rien dans les présentes Conditions ne limite une responsabilité qui ne peut être limitée en
          vertu de la loi applicable, y compris les droits impératifs des consommateurs de l'UE.
        </p>
      </>
    ),
  },
  {
    id: "indemnification",
    heading: "14. Indemnisation",
    body: (
      <>
        <p>
          Vous acceptez de défendre, indemniser et tenir Greenwich et son équipe à l'abri de toute réclamation,
          perte ou dépense (y compris les frais juridiques raisonnables) résultant de votre violation des présentes
          Conditions, de votre Contenu Utilisateur, ou de votre interaction avec un organisateur de programme ou un
          autre utilisateur du Service.
        </p>
      </>
    ),
  },
  {
    id: "law",
    heading: "15. Droit applicable et litiges",
    body: (
      <>
        <p>
          Les présentes Conditions sont régies par le droit de la République d'Arménie, sans égard aux principes
          de conflit de lois. Tout litige découlant des présentes Conditions ou du Service relève de la compétence
          exclusive des tribunaux compétents de la République d'Arménie.
        </p>
        <p>
          <strong>Si vous résidez dans l'Union européenne</strong>, ce choix de droit et de juridiction ne vous
          prive pas de la protection que vous assurent les dispositions auxquelles il ne peut être dérogé par
          accord en vertu du droit de votre pays de résidence habituelle (notamment le droit de la consommation).
          Vous conservez le droit d'engager une procédure devant les tribunaux de votre pays de résidence lorsque
          le droit de la consommation applicable vous y autorise.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    heading: "16. Modifications des présentes Conditions",
    body: (
      <>
        <p>
          Nous pouvons mettre à jour les présentes Conditions de temps à autre. En cas de modification substantielle,
          nous publierons les Conditions mises à jour avec une nouvelle date de « Dernière mise à jour » et, le cas
          échéant, vous en informerons (par exemple par e-mail ou notification dans l'application). Le fait de
          continuer à utiliser le Service après l'entrée en vigueur des modifications signifie que vous acceptez
          les Conditions mises à jour.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    heading: "17. Nous contacter",
    body: (
      <>
        <p>
          Pour toute question concernant les présentes Conditions, écrivez à{" "}
          <a href="mailto:greenwicharmenia@gmail.com">greenwicharmenia@gmail.com</a> ou appelez le
          +374 94 577 585.
        </p>
      </>
    ),
  },
];

function Terms() {
  return (
    <LegalPage
      titleEn="Terms of Service"
      titleHy="Օգտագործման պայմաններ"
      titleFr="Conditions d'utilisation"
      updatedEn={UPDATED_EN}
      updatedHy={UPDATED_HY}
      updatedFr={UPDATED_FR}
      sectionsEn={sectionsEn}
      sectionsHy={sectionsHy}
      sectionsFr={sectionsFr}
      seoTitleKey="pages.seo.terms.title"
      seoDescKey="pages.seo.terms.description"
      seoKeywordsKey="pages.seo.terms.keywords"
      seoPath="/terms"
    />
  );
}

export default Terms;
